import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid
} from '@mui/material';
import {
  CheckCircle,
  Star,
  StarBorder,
  Close,
  PhotoCamera
} from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

function CompletedTasksCard({ tasks, familyMembers, isParent }) {
  const [selectedTask, setSelectedTask] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setOpenDialog(true);
  };

  const handleToggleFavorite = async (taskId, currentFavorite) => {
    try {
      await updateDoc(doc(db, 'tasks', taskId), {
        favorited: !currentFavorite
      });
      // Update local state
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask({ ...selectedTask, favorited: !currentFavorite });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const getAssignedNames = (assignedTo) => {
    if (!assignedTo || assignedTo.length === 0) return 'Unassigned';
    return assignedTo
      .map(uid => {
        const member = familyMembers.find(m => m.id === uid);
        return member?.displayName || 'Unknown';
      })
      .join(', ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} hr ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Filter completed tasks from last 30 days
  const recentTasks = tasks.filter(task => {
    if (task.status !== 'completed') return false;
    if (!task.approvedAt) return false;
    const approvedDate = new Date(task.approvedAt);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return approvedDate > thirtyDaysAgo;
  }).sort((a, b) => new Date(b.approvedAt) - new Date(a.approvedAt));

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle color="success" />
              Completed Tasks
            </Typography>
            <Chip 
              label={`${recentTasks.length} completed`} 
              color="success" 
              size="small" 
            />
          </Box>

          {recentTasks.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No completed tasks yet. Get started! 🚀
            </Typography>
          ) : (
            <List>
              {recentTasks.slice(0, 5).map((task) => (
                <ListItem
                  key={task.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => handleViewTask(task)}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <CheckCircle />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {task.title}
                        {task.favorited && <Star sx={{ fontSize: 16, color: 'warning.main' }} />}
                        {(task.photos?.before || task.photos?.after) && (
                          <PhotoCamera sx={{ fontSize: 16, color: 'info.main' }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        {getAssignedNames(task.assignedTo)} • ${task.allowance?.toFixed(2) || '0.00'} • {formatDate(task.approvedAt)}
                      </>
                    }
                  />
                  <Chip
                    icon={<CheckCircle />}
                    label="Completed"
                    color="success"
                    size="small"
                  />
                </ListItem>
              ))}
              {recentTasks.length > 5 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  +{recentTasks.length - 5} more completed tasks
                </Typography>
              )}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Task Detail Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        {selectedTask && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle color="success" />
                  Task Completed
                </Box>
                {isParent && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(selectedTask.id, selectedTask.favorited);
                    }}
                    color={selectedTask.favorited ? 'warning' : 'default'}
                    sx={{ minWidth: 48, minHeight: 48 }}
                  >
                    {selectedTask.favorited ? <Star /> : <StarBorder />}
                  </IconButton>
                )}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  {selectedTask.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  {selectedTask.description}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Completed by:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {getAssignedNames(selectedTask.assignedTo)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Earned:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      ${selectedTask.allowance?.toFixed(2) || '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Completed:
                    </Typography>
                    <Typography variant="body1">
                      {selectedTask.completedAt ? new Date(selectedTask.completedAt).toLocaleString() : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Approved:
                    </Typography>
                    <Typography variant="body1">
                      {selectedTask.approvedAt ? new Date(selectedTask.approvedAt).toLocaleString() : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Photos */}
              {(selectedTask.photos?.before || selectedTask.photos?.after) && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Photos 📸
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedTask.photos?.before && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Before
                        </Typography>
                        <Box
                          component="img"
                          src={selectedTask.photos.before}
                          alt="Before"
                          sx={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: 2,
                            border: 2,
                            borderColor: 'divider'
                          }}
                        />
                      </Grid>
                    )}
                    {selectedTask.photos?.after && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          After
                        </Typography>
                        <Box
                          component="img"
                          src={selectedTask.photos.after}
                          alt="After"
                          sx={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: 2,
                            border: 2,
                            borderColor: 'divider'
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} startIcon={<Close />}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}

export default CompletedTasksCard;













